// Bureau — hooks React Query de /api/bureau.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BureauConfigDTO,
  BureauFollowDTO,
  BureauFollowType,
  BureauMeDTO,
  BureauOverviewDTO,
  BureauStockDTO,
  BureauStockLevel,
  BureauTodayDTO,
} from '@pointage/shared';
import { apiClient } from './client.js';
import { useMe } from './hooks.js';

export class BureauApiError extends Error {
  code?: string;
  constructor(status: number, code?: string, message?: string) {
    super(message ?? `bureau ${status}${code ? ` ${code}` : ''}`);
    this.code = code;
  }
}

async function call<T>(p: Promise<{ status: number; data: unknown }>): Promise<T> {
  const res = await p;
  if (res.status >= 400) {
    const d = (res.data ?? {}) as { error?: string; message?: string };
    throw new BureauApiError(res.status, d.error, d.message);
  }
  return res.data as T;
}

const K = {
  me: ['bureau', 'me'] as const,
  today: (d: string) => ['bureau', 'today', d] as const,
  follows: (s: 'open' | 'closed') => ['bureau', 'follows', s] as const,
  stock: ['bureau', 'stock'] as const,
  overview: ['bureau', 'overview'] as const,
  config: ['bureau', 'config'] as const,
};

/** Accès au Bureau pour la session (bureau.view effectif). */
export function useBureauAccess() {
  const me = useMe();
  const perms = me.data?.permissions ?? [];
  return { view: perms.includes('bureau.view'), manage: perms.includes('bureau.manage') };
}

export function useBureauMe(enabled = true) {
  return useQuery<BureauMeDTO>({
    queryKey: K.me,
    queryFn: () => call(apiClient.get('/bureau/me')),
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useBureauToday(date: string) {
  return useQuery<BureauTodayDTO>({
    queryKey: K.today(date),
    queryFn: () => call(apiClient.get('/bureau/today', { params: { date } })),
    refetchInterval: 60_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['bureau'] });
}

export function useBureauCheck(date: string) {
  const qc = useQueryClient();
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (v: { itemId: string; done: boolean; photo?: Blob }) => {
      if (!v.done) return call<BureauTodayDTO>(apiClient.delete(`/bureau/checks/${v.itemId}`, { params: { date } }));
      if (v.photo) {
        const fd = new FormData();
        fd.append('itemId', v.itemId);
        fd.append('date', date);
        fd.append('photo', v.photo, 'photo.jpg');
        return call<BureauTodayDTO>(apiClient.post('/bureau/checks', fd));
      }
      return call<BureauTodayDTO>(apiClient.post('/bureau/checks', { itemId: v.itemId, date }));
    },
    onSuccess: (data) => {
      qc.setQueryData(K.today(date), data);
      void inv();
    },
  });
}

export function useBureauReport() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: { momentId: string; kind: string; note?: string }) => call(apiClient.post('/bureau/reports', v)),
    onSuccess: () => void inv(),
  });
}

export function useBureauFollows(status: 'open' | 'closed' = 'open') {
  return useQuery<{ items: BureauFollowDTO[] }>({
    queryKey: K.follows(status),
    queryFn: () => call(apiClient.get('/bureau/follows', { params: { status } })),
    refetchInterval: status === 'open' ? 60_000 : false,
  });
}

export interface NewFollow {
  type: Exclude<BureauFollowType, 'ACHAT'>;
  title: string;
  note?: string;
  dueDate?: string;
  eventDate?: string;
  steps?: string[];
}

export function useCreateFollow() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: NewFollow) => call<{ follow: BureauFollowDTO }>(apiClient.post('/bureau/follows', v)),
    onSuccess: () => void inv(),
  });
}

export function usePatchFollow() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...v }: { id: string; title?: string; note?: string | null; dueDate?: string | null; waitingOn?: string | null }) =>
      call<{ follow: BureauFollowDTO }>(apiClient.patch(`/bureau/follows/${id}`, v)),
    onSuccess: () => void inv(),
  });
}

export function useToggleStep() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (v: { followId: string; stepId: string; done: boolean }) =>
      call<{ follow: BureauFollowDTO }>(apiClient.post(`/bureau/follows/${v.followId}/steps/${v.stepId}`, { done: v.done })),
    onSuccess: () => void inv(),
  });
}

export function useNudgeFollow() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => call<{ follow: BureauFollowDTO }>(apiClient.post(`/bureau/follows/${id}/nudge`)),
    onSuccess: () => void inv(),
  });
}

export function useCancelFollow() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => call<{ follow: BureauFollowDTO }>(apiClient.post(`/bureau/follows/${id}/cancel`)),
    onSuccess: () => void inv(),
  });
}

export function useBureauStock() {
  return useQuery<BureauStockDTO>({ queryKey: K.stock, queryFn: () => call(apiClient.get('/bureau/stock')) });
}

export function useSetStockLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; level: BureauStockLevel }) => call<BureauStockDTO>(apiClient.patch(`/bureau/stock/${v.id}`, { level: v.level })),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: K.stock });
      const prev = qc.getQueryData<BureauStockDTO>(K.stock);
      if (prev) qc.setQueryData(K.stock, { ...prev, items: prev.items.map((i) => (i.id === v.id ? { ...i, level: v.level } : i)) });
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(K.stock, ctx.prev); },
    onSuccess: (data) => qc.setQueryData(K.stock, data),
  });
}

export function usePurchaseList() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: () => call<BureauStockDTO>(apiClient.post('/bureau/stock/list')),
    onSuccess: () => void inv(),
  });
}

export function useBureauOverview(enabled: boolean) {
  return useQuery<BureauOverviewDTO>({
    queryKey: K.overview,
    queryFn: () => call(apiClient.get('/bureau/overview')),
    enabled,
    refetchInterval: 120_000,
  });
}

export function useBureauConfig(enabled: boolean) {
  return useQuery<BureauConfigDTO>({ queryKey: K.config, queryFn: () => call(apiClient.get('/bureau/config')), enabled });
}

/** Mutation de réglage générique : chaque route renvoie la config entière. */
export function useBureauConfigMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { method: 'post' | 'patch' | 'put'; path: string; body: unknown }) =>
      call<BureauConfigDTO>(apiClient[v.method](`/bureau/config${v.path}`, v.body)),
    onSuccess: (data) => {
      qc.setQueryData(K.config, data);
      void qc.invalidateQueries({ queryKey: ['bureau'], predicate: (q) => q.queryKey[1] !== 'config' });
    },
  });
}

/** Réduit une photo de téléphone (~4 Mo) à un JPEG de 1280 px max. */
export async function compressPhoto(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1280 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.72));
    return blob ?? file;
  } catch {
    return file;
  }
}
